import React, { useEffect } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Divider, Dropdown, Menu } from 'antd';
import styled from 'styled-components/macro';
import { useTranslation } from '../../../../i18n/useTranslation';

import { useGetGlossaryTermQuery } from '../../../../graphql/glossaryTerm.generated';
import { useGetGlossaryNodeQuery } from '../../../../graphql/glossaryNode.generated';
import { EntityType, TermRelationshipType } from '../../../../types.generated';

import { RelatedTermTypes } from '../../shared/components/styled/search/types';
import { useEntityRegistry } from '../../../useEntityRegistry';
import useRemoveRelatedEntity from './useRemoveRelatedEntity';

import { ReactComponent as MoreAction } from '../../../../images/more-vert.svg';
import GlossaryTermSkeleton from './GlossaryTermSkeleton';
import { PreviewType } from '../../Entity';

const Profile = styled.div`
    display: flex;
    padding: 24px;
`;

const BrowseDropDown = styled(Dropdown)`
    display: flex;
    cursor: pointer;
    align-items: center;
`;

const MenuItem = styled.div`
    padding: 0 4px;
    color: #262626;
`;

interface Props {
    urn: string;
    isLoading?: boolean;
    entityType: EntityType;
    relationshipType?: TermRelationshipType | RelatedTermTypes;
    onLoadingChange?: (isLoading: boolean) => void;
}

const getEntityConfig = (entityType) => {
    const defaultConfig = {
        useGetGlossaryQuery: useGetGlossaryTermQuery,
        dataKey: 'glossaryTerm',
        entityName: 'Term',
    };

    // Choose the correct entity type based on the entity type
    switch (entityType) {
        case EntityType.GlossaryTerm:
            // For simple glossary term, we use the default config
            return defaultConfig;

        case EntityType.GlossaryNode:
            return {
                useGetGlossaryQuery: useGetGlossaryNodeQuery,
                dataKey: 'glossaryNode',
                entityName: 'Term Group',
            };

        default:
            return defaultConfig;
    }
};

function LinkedEntity(props: Props) {
    const { t } = useTranslation();
    const { urn, relationshipType, entityType, isLoading, onLoadingChange } = props;
    const { useGetGlossaryQuery, dataKey, entityName } = getEntityConfig(entityType);

    const { data, loading } = useGetGlossaryQuery({ variables: { urn } });
    const entityRegistry = useEntityRegistry();

    let displayName = '';

    if (data) {
        displayName = entityRegistry.getDisplayName(entityType, data[dataKey]);
    }

    const { onRemove } = useRemoveRelatedEntity(urn, relationshipType, displayName);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    // Show skeleton when loading
    if (isLoading) return <GlossaryTermSkeleton />;

    // Add relationshipType to the preview props
    const getPreviewProps = () => ({
        ...data?.[dataKey],
        relationshipType,
    });

    return (
        <>
            <Profile>
                {entityRegistry.renderPreview(entityType, PreviewType.PREVIEW, getPreviewProps())}
                <BrowseDropDown
                    overlay={
                        <Menu>
                            <Menu.Item key="0" aria-label={t('Remove {{entityName}}', { entityName })}>
                                <MenuItem onClick={onRemove}>
                                    <DeleteOutlined /> &nbsp; {t('Remove {{entityName}}', { entityName })}
                                </MenuItem>
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button
                        type="text"
                        tabIndex={0}
                        icon={<MoreAction />}
                        title={t('More Actions')}
                        className="icon-button ma-0"
                        aria-label={t('More Actions')}
                    />
                </BrowseDropDown>
            </Profile>
            <Divider className="ma-0" />
        </>
    );
}

export default LinkedEntity;
